import { $generateNodesFromSerializedNodes } from '@lexical/clipboard'
import type { ContentAST, EmbedReference } from '@shared/ast/astTypes'
import { generateBlockId } from '@shared/ast/astUtils'
import { inlineEmbedContent, isInlineEmbedReference } from '@shared/ast/embedReference'
import {
  $getRoot,
  $isElementNode,
  $isRootNode,
  $splitNode,
  type ElementNode,
  type LexicalNode,
  type SerializedElementNode,
} from 'lexical'

import { $createCustomEmbedNode, $isCustomEmbedNode, type CustomEmbedNode } from './nodes/CustomEmbedNode'
import {
  $createCustomInlineEmbedNode,
  $isCustomInlineEmbedNode,
  type CustomInlineEmbedNode,
} from './nodes/CustomInlineEmbedNode'
import { $createCustomParagraphNode } from './nodes/CustomParagraphNode'
import { astToLexical } from '../../../transforms/ast/lexicalBridge'

type EmbedNode = CustomEmbedNode | CustomInlineEmbedNode
type IdentifiedNode = LexicalNode & { getBlockId(): string; setBlockId(id: string): unknown }

function identified(node: LexicalNode): node is IdentifiedNode {
  return (
    typeof (node as Partial<IdentifiedNode>).getBlockId === 'function' &&
    typeof (node as Partial<IdentifiedNode>).setBlockId === 'function'
  )
}

function visit(node: LexicalNode, callback: (node: LexicalNode) => void) {
  callback(node)
  if ($isElementNode(node)) {
    node.getChildren().forEach((child) => visit(child, callback))
  }
}

function removeEmptyBranch(node: ElementNode) {
  let current: ElementNode | null = node
  while (current && !$isRootNode(current) && current.isAttached() && current.getChildrenSize() === 0) {
    const parent: ElementNode | null = current.getParent()
    current.remove()
    current = parent
  }
}

/** Split existing nodes, never reconstruct surrounding prose from fetched text. */
export function $inlineEmbedToCard(node: CustomInlineEmbedNode, source = node.getSource()): CustomEmbedNode {
  const parent = node.getParentOrThrow()
  const [left, right] = $splitNode(parent, node.getIndexWithinParent())
  const card = $createCustomEmbedNode(generateBlockId(), source)
  right.insertBefore(card)
  const newParent = node.getParentOrThrow()
  node.remove()
  removeEmptyBranch(parent)
  removeEmptyBranch(newParent)

  // $splitNode copies each split ancestor, including custom block ids. Only ids
  // still used in the left branch need replacing; moved original siblings keep theirs.
  if (right.isAttached()) {
    const used = new Set<string>()
    for (const sibling of $getRoot().getChildren()) {
      if (sibling !== right) {
        visit(sibling, (entry) => {
          if (identified(entry)) {
            used.add(entry.getBlockId())
          }
        })
      }
    }
    visit(right, (entry) => {
      if (identified(entry)) {
        if (used.has(entry.getBlockId())) {
          entry.setBlockId(generateBlockId())
        }
        used.add(entry.getBlockId())
      }
    })
  }
  if (!left?.isAttached() && !right.isAttached() && identified(parent)) {
    card.setBlockId(parent.getBlockId())
  }
  if (!card.getNextSibling()) {
    card.insertAfter($createCustomParagraphNode(generateBlockId()))
  }
  card.selectNext()
  return card
}

export function $cardEmbedToInline(node: CustomEmbedNode, source = node.getSource(), dir?: 'rtl' | 'ltr') {
  const paragraph = $createCustomParagraphNode(node.getBlockId())
  if (dir) {
    paragraph.setDir(dir)
  }
  paragraph.append($createCustomInlineEmbedNode(source))
  node.replace(paragraph)
  paragraph.selectEnd()
}

/** Detach is the only action allowed to put resolved source prose into editor state. */
export function $detachEmbed(node: EmbedNode, content: ContentAST) {
  if ($isCustomInlineEmbedNode(node)) {
    const inline = inlineEmbedContent(content)
    if (!inline) {
      return
    }
    const serialized = astToLexical({ ...content, blocks: [{ type: 'paragraph', id: 'local', content: inline }] })
    const children = (serialized.root.children[0] as SerializedElementNode).children
    for (const child of $generateNodesFromSerializedNodes(children)) {
      node.insertBefore(child)
    }
    node.selectNext()
    node.remove()
    return
  }
  if ($isCustomEmbedNode(node)) {
    const replacements = $generateNodesFromSerializedNodes(astToLexical(content).root.children)
    for (const replacement of replacements) {
      visit(replacement, (entry) => {
        if (identified(entry)) {
          entry.setBlockId(generateBlockId())
        }
      })
      node.insertBefore(replacement)
    }
    if (replacements[0] && identified(replacements[0])) {
      replacements[0].setBlockId(node.getBlockId())
    }
    node.selectNext()
    node.remove()
  }
}

export function $setEmbedSource(node: EmbedNode, source: EmbedReference, content: ContentAST): boolean {
  if ($isCustomInlineEmbedNode(node) && (!isInlineEmbedReference(source) || !inlineEmbedContent(content))) {
    $inlineEmbedToCard(node, source)
    return true
  }
  node.setSource(source)
  return false
}
